// The Auto-apply instruction, read back as a card.
//
// The Applications page speaks to the assistant in a fixed shape ("AUTO-APPLY
// MODE. … FACTS — … THIS JOB — jobId: …"). That text is for the model; shown
// in the chat as if the person had typed it, it read as a dirty dump of ids
// and keys (founder, 2026-09-24). The chat draws such a message as a small
// card instead — what is being answered, for which job — and the words the
// model receives stay exactly as they are.

const HEAD = /^AUTO-APPLY MODE\b/;
const DONE = /^AUTO-APPLY DONE\b/;
const WORKING = /^AUTO-APPLY WORKING\b/;
const JOB = /^AUTO-APPLY JOB\b/;

/**
 * One job's card, kept live: the same steps its card on the page shows, and
 * the questions left for the person — each a button that opens the job's
 * card on the page (founder, 2026-09-25: "nothing else is happening in the
 * chat").
 */
export const autoApplyJobText = ({ jobId, company, steps = [], questions = [] }) => `AUTO-APPLY JOB\n${JSON.stringify({
  jobId: String(jobId || ''),
  company: String(company || '').slice(0, 80),
  steps: steps.slice(-8).map(x => ({ text: String(x.text || '').slice(0, 160), state: x.state || 'done' })),
  questions: questions.slice(0, 6).map(q => String(q || '').slice(0, 160)),
})}`;

/** The card posted the moment a pass starts; replaced by the DONE card. */
export const autoApplyWorkingText = (jobs) => `AUTO-APPLY WORKING\n${JSON.stringify({ jobs: Math.max(1, Number(jobs) || 1) })}`;

/** The page's summary of a server pass, as text for the transcript. */
export const autoApplyDoneText = (summary, items = []) => `AUTO-APPLY DONE\n${JSON.stringify({
  saved: summary?.saved?.length || 0,
  approved: (summary?.approved?.length || 0) + (summary?.fixed?.length || 0),
  picked: summary?.picked?.length || 0,
  left: summary?.left || 0,
  // The questions that are the person's, by job — the card names them
  // rather than counting them, so it is an answer and not a number.
  items: (Array.isArray(items) ? items : []).slice(0, 8).map(i => ({ company: String(i.company || '').slice(0, 60), label: String(i.label || '').slice(0, 140) })),
})}`;

/** null when the text is an ordinary message; else what the card shows. */
export function autoApplyCard(content) {
  const text = String(content || '').trim();
  if (JOB.test(text)) {
    let s = {};
    try { s = JSON.parse(text.slice(text.indexOf('\n') + 1) || '{}'); } catch { s = {}; }
    const steps = Array.isArray(s.steps) ? s.steps : [];
    return { jobCard: true, jobId: s.jobId || null, company: s.company || '', steps, questions: Array.isArray(s.questions) ? s.questions : [], working: steps.some(x => x.state === 'active'), facts: [], job: null };
  }
  if (WORKING.test(text)) {
    let s = {};
    try { s = JSON.parse(text.slice(text.indexOf('\n') + 1) || '{}'); } catch { s = {}; }
    return { working: true, jobs: Number(s.jobs) || 1, facts: [], job: null };
  }
  if (DONE.test(text)) {
    // Since 2026-09-24 the answering is a server job; the chat shows only
    // what it did.
    let s = {};
    try { s = JSON.parse(text.slice(text.indexOf('\n') + 1) || '{}'); } catch { s = {}; }
    return { done: true, saved: s.saved || 0, approved: s.approved || 0, picked: s.picked || 0, left: s.left || 0, items: Array.isArray(s.items) ? s.items : [], facts: [], job: null };
  }
  if (!HEAD.test(text)) return null;
  const facts = [];
  let job = null;
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    // Both shapes the page has sent: "THIS JOB — jobId: …" now, "- jobId: …" before.
    const jobHead = /^(?:THIS JOB — |- )jobId: (\S+) — (.+?) — (.+)$/.exec(line);
    if (jobHead) { job = { id: jobHead[1], company: jobHead[2], title: jobHead[3], items: [] }; continue; }
    const item = /^· (?:\[([^\]]+)\] )?"([^"]+)"(?: \/ "[^"]+")*(?: \((\d+) jobs?\))?/.exec(line);
    if (!item) continue;
    if (job) job.items.push({ label: item[2] });
    else facts.push({ key: item[1] || null, label: item[2], jobs: item[3] ? Number(item[3]) : 1 });
  }
  // The per-job lines carry the intent key before the label; the fact lines
  // carry it too. Either way the label is the readable part.
  return { facts, job };
}

/** The person's questions, one line each, grouped by job. */
export function autoApplyLines(card) {
  if (!card?.done || !card.items?.length) return [];
  const byJob = new Map();
  for (const i of card.items) { const l = byJob.get(i.company) || []; l.push(i.label); byJob.set(i.company, l); }
  const lines = [...byJob].map(([company, labels]) => `${company}: ${labels.join(' · ')}`);
  const shown = card.items.length;
  if (card.left > shown) lines.push(`and ${card.left - shown} more`);
  return lines;
}

/** One short line for the card's second row. */
export function autoApplySummary(card) {
  const parts = [];
  if (card.jobCard) return card.steps.length ? card.steps[card.steps.length - 1].text : 'Starting…';
  if (card.working) return `Answering ${card.jobs} ${card.jobs === 1 ? 'job' : 'jobs'}…`;
  if (card.done) {
    const n = (w, k) => `${k} ${w}${k === 1 ? '' : 's'}`;
    if (card.saved) parts.push(`${n('question', card.saved)} answered`);
    if (card.approved) parts.push(`${n('draft', card.approved)} approved`);
    if (card.picked) parts.push(`${n('option', card.picked)} picked`);
    const did = parts.join(', ') || (card.left ? 'Nothing more I can answer' : '');
    const rest = card.left ? `${n('question', card.left)} ${card.left === 1 ? 'is' : 'are'} yours` : '';
    return [did, rest].filter(Boolean).join(' · ') || 'Nothing left to answer';
  }
  if (card.facts.length) parts.push(`${card.facts.length} ${card.facts.length === 1 ? 'question' : 'questions'} several forms ask`);
  if (card.job) parts.push(`${card.job.items.length} for ${card.job.company}`);
  return parts.join(' · ') || 'Nothing left to answer';
}

/**
 * A reply with the model's echo of its own tool transcript cut off. The
 * server no longer stores that block; this covers replies stored before,
 * and anything that slips through a stream.
 */
export const stripToolEcho = (text) => String(text || '').replace(/\n*\[Tools you ran earlier in this conversation[\s\S]*?\]\s*$/, '').trimEnd();
